import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  SpinnerGap, Copy, X, Buildings, Briefcase,
  Heart, Palette, Megaphone, HandHeart, Cpu, ShoppingCart,
  MagnifyingGlass, Check
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  Technology: Cpu, Commerce: ShoppingCart, Coaching: Heart,
  "Professional Services": Briefcase, Agency: Megaphone,
  Creative: Palette, Impact: HandHeart, General: Buildings,
};

const CATEGORY_COLORS = {
  Technology: "#3B82F6", Commerce: "#F59E0B", Coaching: "#EC4899",
  "Professional Services": "#8B5CF6", Agency: "#FF4400",
  Creative: "#06B6D4", Impact: "#10B981", General: "#6B7280",
};

export function StrategyTemplatePickerModal({ isOpen, onClose, onCloned }) {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [cloning, setCloning] = useState(null);
  const [cloned, setCloned] = useState(null);

  const loadTemplates = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const resp = await api.get("/strategy/templates");
      setTemplates(resp.data?.templates || []);
      setCategories(resp.data?.categories || []);
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [api, isOpen]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (isOpen) {
      setCloned(null);
      setSearchQuery("");
      setSelectedCategory("");
    }
  }, [isOpen]);

  const handleClone = async (templateId, templateName) => {
    setCloning(templateId);
    try {
      const resp = await api.post(`/strategy/templates/${templateId}/clone`);
      const profile = resp.data?.strategy_profile;
      setCloned(templateId);
      toast.success(`"${templateName}" added to your profiles`);
      setTimeout(() => {
        onCloned?.(profile);
        onClose();
      }, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clone template");
    } finally {
      setCloning(null);
    }
  };

  const filtered = templates.filter((t) => {
    const matchesCat = !selectedCategory || t.category === selectedCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q) || t.category?.toLowerCase().includes(q);
    return matchesCat && matchesSearch;
  });

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-3xl max-h-[85vh] bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          data-testid="template-picker-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
            <div>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[var(--text-primary)]">Start from a Template</h2>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">Choose an industry template and customize it for your business</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-muted)]" data-testid="template-picker-close">
              <X size={18} />
            </button>
          </div>

          {/* Filters */}
          <div className="px-5 py-3 border-b border-[var(--border)] space-y-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                className="w-full pl-9 pr-4 py-2 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="template-picker-search"
              />
            </div>
            <div className="flex gap-1.5 overflow-x-auto">
              <button
                onClick={() => setSelectedCategory("")}
                className={`px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors ${
                  !selectedCategory ? "bg-[#FF4400] text-white" : "bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat === selectedCategory ? "" : cat)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === cat ? "bg-[#FF4400] text-white" : "bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Template List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {loading && (
              <div className="flex justify-center py-12">
                <SpinnerGap size={24} className="animate-spin text-[#FF4400]" />
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="text-center py-12 text-sm text-[var(--text-muted)]">
                No templates match your search.
              </div>
            )}

            {!loading && filtered.map((t) => {
              const CatIcon = CATEGORY_ICONS[t.category] || Buildings;
              const catColor = CATEGORY_COLORS[t.category] || "#6B7280";
              const isCloned = cloned === t.template_id;

              return (
                <div
                  key={t.template_id}
                  className="flex items-start gap-3 p-3 rounded-xl bg-[var(--bg-base)] border border-[var(--border)] hover:border-[var(--border-strong)] transition-colors"
                  data-testid={`template-picker-item-${t.template_id}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: `${catColor}15` }}
                  >
                    <CatIcon size={18} weight="duotone" style={{ color: catColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className="text-sm font-semibold text-[var(--text-primary)]">{t.name}</h4>
                      <span className="text-[9px] font-medium uppercase tracking-wider" style={{ color: catColor }}>{t.category}</span>
                    </div>
                    <p className="text-[11px] text-[var(--text-secondary)] line-clamp-2">{t.description}</p>
                    <p className="text-[10px] text-[var(--text-muted)] mt-1 line-clamp-1"><span className="font-medium">ICP:</span> {t.icp?.substring(0, 100)}...</p>
                  </div>
                  <Button
                    size="sm"
                    disabled={cloning === t.template_id || isCloned}
                    onClick={() => handleClone(t.template_id, t.name)}
                    className={`shrink-0 text-xs h-8 px-3 ${
                      isCloned
                        ? "bg-green-600 hover:bg-green-600 text-white"
                        : "bg-[#FF4400] hover:bg-[#FF6633] text-white"
                    }`}
                    data-testid={`template-picker-clone-${t.template_id}`}
                  >
                    {cloning === t.template_id ? (
                      <SpinnerGap size={14} className="animate-spin" />
                    ) : isCloned ? (
                      <><Check size={14} className="mr-1" /> Added</>
                    ) : (
                      <><Copy size={14} className="mr-1" /> Use</>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
